import type React from "react";
import { useMemo } from "react";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { format, isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { useGetTherapistActivePatients } from "api/services/therapist/hooks";
import { useProfileContext } from "contexts/ProfileContext";
import { NoGraphData } from "routes/admin/components/NoGraphData";
import { fillMissingMonths, getStartTimeSixMonthsAgo } from "routes/admin/components/utils";
import type { BarConfig } from "shared/atoms/Charts";
import { StackedBarChart } from "shared/atoms/Charts";
import { GraphSkeleton } from "shared/atoms/SkeletonLayout";
import { WidgetBox } from "shared/containers/WidgetBox";

export const ActivePatients: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { profile } = useProfileContext();

  invariant(profile, "Profile must be defined");

  const { data: activePatientsData, isLoading } = useGetTherapistActivePatients({
    params: {
      start_time: getStartTimeSixMonthsAgo(),
    },
  });
  const data = fillMissingMonths(activePatientsData?.items || [], ["total_hep", "total_rtm"]);

  const stackTotals = useMemo(() => {
    const totals: Record<string, number> = {};
    data.forEach(item => {
      totals[item.month] = (item.total_hep || 0) + (item.total_rtm || 0);
    });
    return totals;
  }, [data]);

  const hasData = data.some(item => (item.total_hep || 0) > 0 || (item.total_rtm || 0) > 0);

  const bars: BarConfig[] = [
    { dataKey: "total_hep", name: t("invite.treatment_type_options.hep"), color: theme.colors.redesign.b100 },
    { dataKey: "total_rtm", name: t("invite.treatment_type_options.rtm"), color: theme.colors.redesign.b80 },
  ];

  return (
    <WidgetBox
      title={t("admin.active_patients.title")}
      description={t("admin.active_patients.description")}
      icon={<FontAwesomeIcon icon={faUsers} />}
      color="blue"
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "240px" }}>
        {isLoading && <GraphSkeleton />}
        {!isLoading && !hasData && <NoGraphData title={t("admin.active_patients.no_data")} />}
        {!isLoading && hasData && (
          <StackedBarChart
            data={data}
            bars={bars}
            tooltipProps={{
              color: theme.colors.redesign.b100,
              labelFormatter: (label: string) => {
                const date = parseISO(label);
                return isValid(date) ? format(date, "MMMM yyyy") : "";
              },
              stackTotals,
            }}
            height={240}
            xAxisDataKey="month"
            xAxisProps={{
              tickFormatter: value => {
                const date = parseISO(value);
                return isValid(date) ? format(date, "MMM") : "";
              },
            }}
            hideLegend
          />
        )}
      </Box>
    </WidgetBox>
  );
};
